import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const DialogContext = createContext(undefined);

const mergeClassNames = (...classes) => classes.filter(Boolean).join(' ');

const useDialogContext = (componentName) => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error(`${componentName} debe usarse dentro de un <Dialog>.`);
  }
  return context;
};

export const Dialog = ({ children, open, defaultOpen = false, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = typeof open === 'boolean';
  const currentOpen = isControlled ? open : internalOpen;

  const setOpen = useCallback(
    (value) => {
      if (!isControlled) {
        setInternalOpen(value);
      }
      if (onOpenChange) {
        onOpenChange(value);
      }
    },
    [isControlled, onOpenChange]
  );

  const contextValue = useMemo(
    () => ({ open: currentOpen, setOpen }),
    [currentOpen, setOpen]
  );

  return <DialogContext.Provider value={contextValue}>{children}</DialogContext.Provider>;
};

export const DialogTrigger = ({ children, asChild = false, className = '', ...props }) => {
  const { setOpen } = useDialogContext('DialogTrigger');
  const { onClick: propsOnClick, ...rest } = props;

  const handleClick = (event) => {
    if (typeof propsOnClick === 'function') {
      propsOnClick(event);
    }
    if (event.defaultPrevented) return;
    setOpen(true);
  };

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...rest,
      className: mergeClassNames(children.props.className, className),
      onClick: (event) => {
        if (typeof children.props.onClick === 'function') {
          children.props.onClick(event);
        }
        handleClick(event);
      },
    });
  }

  return (
    <button
      type="button"
      {...rest}
      className={mergeClassNames(
        'dialog-trigger inline-flex items-center justify-center rounded-md bg-azul-lago px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-azul-lago/90 focus:outline-none focus:ring-2 focus:ring-azul-lago',
        className
      )}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

export const DialogContent = ({
  children,
  className = '',
  overlayClassName = '',
  closeOnOverlayClick = true,
  ...props
}) => {
  const { open, setOpen } = useDialogContext('DialogContent');
  const contentRef = useRef(null);
  const previouslyFocusedElement = useRef(null);
  const { onClick: propsOnClick, ...rest } = props;

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocusedElement.current = document.activeElement;
    const focusable = contentRef.current?.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocusedElement.current instanceof HTMLElement) {
        previouslyFocusedElement.current.focus();
      }
    };
  }, [open, setOpen]);

  if (!open) return null;

  const handleOverlayClick = (event) => {
    if (!closeOnOverlayClick) return;
    if (event.target === event.currentTarget) {
      setOpen(false);
    }
  };

  return (
    <div
      className={mergeClassNames(
        'dialog-overlay fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 px-4 py-6 backdrop-blur-sm',
        overlayClassName
      )}
      onClick={handleOverlayClick}
    >
      <div
        ref={contentRef}
        className={mergeClassNames(
          'dialog-content w-full max-w-lg sm:max-w-xl lg:max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-azul-oscuro/10 bg-white p-5 text-azul-oscuro shadow-2xl sm:p-7',
          className
        )}
        role="dialog"
        aria-modal="true"
        {...rest}
        onClick={(event) => {
          event.stopPropagation();
          if (typeof propsOnClick === 'function') {
            propsOnClick(event);
          }
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const DialogHeader = ({ children, className = '', ...props }) => {
  return (
    <div
      className={mergeClassNames(
        'dialog-header mb-4 border-b border-azul-oscuro/20 pb-3',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export const DialogTitle = ({ children, className = '', ...props }) => {
  return (
    <h2
      className={mergeClassNames(
        'dialog-title text-2xl font-semibold text-azul-oscuro',
        className
      )}
      {...props}
    >
      {children}
    </h2>
  );
};
